import React, { useState, useEffect } from "react";
import {
  Link,
  NavLink,
  Route,
  Routes,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import Stack from "@mui/material/Stack";
import Drawer from "@mui/material/Drawer";
import AppBar from "@mui/material/AppBar";
import CssBaseline from "@mui/material/CssBaseline";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";

// MUI icons
import Badge from "@mui/material/Badge";
import Business from "@mui/icons-material/Business";
import AirlineSeatReclineExtra from "@mui/icons-material/AirlineSeatReclineExtra";
import DriveEta from "@mui/icons-material/DriveEta";
import Room from "@mui/icons-material/Room";
import FiberManualRecord from "@mui/icons-material/FiberManualRecord";
import ContactSupport from "@mui/icons-material/ContactSupport";
import Tune from "@mui/icons-material/Tune";
import ViewCompact from "@mui/icons-material/ViewCompact";
import DescriptionIcon from "@mui/icons-material/Description";
import MapIcon from "@mui/icons-material/Map";

import logo from "assets/logo.png";
import Start from "views/Start";
import UsersList from "views/UsersList";
import UserInviteView from "views/UserInviteView";
import DriversList from "views/DriversList";
import DriverEdit from "views/DriverEdit";
import CompanyDetail from "views/CompanyDetail";
import CompanyEdit from "views/CompanyEdit";
import CompanyPayment from "views/CompanyPayment";
import VehiclesList from "views/VehiclesList";
import VehicleEdit from "views/VehicleEdit";
import VehicleView from "views/VehicleView";
import PricesList from "views/PricesList";
import OrdersList from "views/OrdersList";
import OrderDetailView from "views/OrderDetailView";
import PreorderDetailView from "views/PreorderDetailView";
import Service from "views/Service";
import Settings from "views/Settings";
import Support from "views/Support";
import Prices from "views/Legal/Prices";
// import MapView from 'views/MapView'
// import Map from 'components/Map'

import styles from "./Dashboard.css";
import AuthService from "services/http/AuthService";
const auth = new AuthService();

const Dashboard = (props) => {
  const { t } = useTranslation();
  const classes = styles();
  const navigate = useNavigate();

  const {
    getMyAnalytics,
    myAnalytics,
    myCompany,
    getMyCompany,
    getUserMe,
    myUser,
  } = props;

  // hooks
  const [loading, setLoading] = useState(true);
  const [open] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [navListOpen, setNavListOpen] = useState({
    company: false,
    drivers: false,
    cars: false,
    orders: false,
  });

  useEffect(() => {
    setLoading(true);
    getMyCompany().then(
      (response) => {
        getUserMe(response?.id).then(
          () => {
            setLoading(false);
            setLoaded(true);
          },
          (_errors) => {
            setLoading(false);
            setLoaded(true);
          },
        );
      },
      (_errors) => {
        setLoading(false);
        setLoaded(true);
      },
    );
  }, [getMyCompany, getUserMe]);

  useEffect(() => {
    if (myCompany) {
      getMyAnalytics().then((_response) => {});
    }
  }, [myCompany, getMyAnalytics]);

  const toggleNavListOpen = (name) => {
    setNavListOpen({
      ...navListOpen,
      [name]: !navListOpen[name],
    });
  };

  const logout = () => {
    auth.logout();
    navigate("/auth/login", { replace: true });
  };

  const DashboardNavLink = React.forwardRef((navProps, ref) => {
    const { className, disabled, onClick, ...rest } = navProps;
    return (
      <NavLink
        ref={ref}
        {...rest}
        aria-disabled={disabled ? true : undefined}
        tabIndex={disabled ? -1 : undefined}
        className={({ isActive }) =>
          `${classes.navLinkRoot} ${className || ""} ${isActive ? classes.listNavActive : ""} ${disabled ? classes.navLinkDisabled : ""}`.trim()
        }
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          if (onClick) {
            onClick(e);
          }
        }}
      />
    );
  });

  return (
    <div className={classes.root}>
      <CssBaseline />

      <AppBar position="fixed" className={classes.appBar} elevation={0}>
        <Stack
          direction="row"
          sx={{
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className={classes.animatedImageContainer}>
            <img src={logo} alt="logo" className={classes.logo} />
          </div>
          <div>
            <button
              type="button"
              className={classes.logoutButton}
              onClick={() => logout()}
            >
              <Typography variant="subtitle2">{t("Kirjaudu ulos")}</Typography>
            </button>
          </div>
        </Stack>
      </AppBar>

      <Drawer
        open={open}
        className={open ? classes.drawerOpen : classes.drawerClose}
        variant="persistent"
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <List component="div">
          <div className={classes.navListLi}>
            <DashboardNavLink to="/dashboard/start">
              <ListItemIcon>
                <ViewCompact className={classes.listMainIcon} />
              </ListItemIcon>
              <ListItemText>
                <Typography className={classes.listItemTypography}>
                  {t("Etusivu")}
                </Typography>
              </ListItemText>
            </DashboardNavLink>
          </div>

          <div className={classes.navListLi}>
            <Badge
              overlap="rectangular"
              classes={{
                anchorOriginTopRightRectangular:
                  classes.anchorOriginTopRightRectangular,
              }}
              badgeContent={
                myUser?.status == 100 &&
                loading == false &&
                (myCompany?.valid == false ||
                  !myCompany?.stripe_user_id ||
                  !myCompany?.seen_price_list)
                  ? "!"
                  : null
              }
              color="error"
            >
              <ListItemButton onClick={() => toggleNavListOpen("company")}>
                <ListItemIcon>
                  <Business className={classes.listMainIcon} />
                </ListItemIcon>
                <ListItemText>
                  <Typography className={classes.listItemTypography}>
                    {t("Yritys")}
                  </Typography>
                </ListItemText>
              </ListItemButton>
            </Badge>
          </div>

          <Collapse in={navListOpen.company} timeout="auto" unmountOnExit>
            <List component="div">
              {myUser?.status == 100 ? (
                <Badge
                  overlap="rectangular"
                  classes={{
                    anchorOriginTopRightRectangular:
                      classes.anchorOriginTopRightRectangularNested,
                  }}
                  badgeContent={
                    myUser?.status == 100 &&
                    loading == false &&
                    myCompany?.valid == false
                      ? "!"
                      : null
                  }
                  color="error"
                >
                  <div className={classes.navListLi}>
                    <DashboardNavLink
                      className={`${classes.listItem} ${classes.nested}`}
                      to="/dashboard/company/edit"
                    >
                      <ListItemIcon className={classes.listIconContainer}>
                        <FiberManualRecord className={classes.listIcon} />
                      </ListItemIcon>
                      <ListItemText inset style={{ padding: 0 }}>
                        <Typography className={classes.listItemTypography}>
                          {t("Perustiedot")}
                        </Typography>
                      </ListItemText>
                    </DashboardNavLink>
                  </div>
                </Badge>
              ) : (
                <div className={classes.navListLi}>
                  <DashboardNavLink
                    className={`${classes.listItem} ${classes.nested}`}
                    to="/dashboard/company/detail"
                  >
                    <ListItemIcon className={classes.listIconContainer}>
                      <FiberManualRecord className={classes.listIcon} />
                    </ListItemIcon>
                    <ListItemText inset style={{ padding: 0 }}>
                      <Typography className={classes.listItemTypography}>
                        {t("Perustiedot")}
                      </Typography>
                    </ListItemText>
                  </DashboardNavLink>
                </div>
              )}

              {myUser?.status == 100 ? (
                <div className={classes.navListLi}>
                  <DashboardNavLink
                    disabled={Boolean(myUser) && myUser.status < 100}
                    className={`${classes.listItem} ${classes.nested}`}
                    to="/dashboard/users"
                  >
                    <ListItemIcon className={classes.listIconContainer}>
                      <FiberManualRecord className={classes.listIcon} />
                    </ListItemIcon>
                    <ListItemText inset style={{ padding: 0 }}>
                      <Typography className={classes.listItemTypography}>
                        {t("Käyttäjät")}
                      </Typography>
                    </ListItemText>
                  </DashboardNavLink>
                </div>
              ) : null}

              {myUser?.status == 100 ? (
                <Badge
                  overlap="rectangular"
                  classes={{
                    anchorOriginTopRightRectangular:
                      classes.anchorOriginTopRightRectangularNested,
                  }}
                  badgeContent={
                    myUser?.status == 100 &&
                    loading == false &&
                    !myCompany?.stripe_user_id
                      ? "!"
                      : null
                  }
                  color="error"
                >
                  <div className={classes.navListLi}>
                    <DashboardNavLink
                      disabled={Boolean(myUser) && myUser.status < 100}
                      className={`${classes.listItem} ${classes.nested}`}
                      to="/dashboard/company/payment"
                    >
                      <ListItemIcon className={classes.listIconContainer}>
                        <FiberManualRecord className={classes.listIcon} />
                      </ListItemIcon>

                      <ListItemText inset style={{ padding: 0 }}>
                        <Typography className={classes.listItemTypography}>
                          {t("Maksutiedot")}
                        </Typography>
                      </ListItemText>
                    </DashboardNavLink>
                  </div>
                </Badge>
              ) : null}

              {myUser?.status == 100 ? (
                <Badge
                  overlap="rectangular"
                  classes={{
                    anchorOriginTopRightRectangular:
                      classes.anchorOriginTopRightRectangularNested,
                  }}
                  badgeContent={
                    myUser?.status == 100 &&
                    loading == false &&
                    !myCompany?.seen_price_list
                      ? "!"
                      : null
                  }
                  color="error"
                >
                  <div className={classes.navListLi}>
                    <DashboardNavLink
                      disabled={false}
                      className={`${classes.listItem} ${classes.nested}`}
                      to="/dashboard/prices/list"
                    >
                      <ListItemIcon className={classes.listIconContainer}>
                        <FiberManualRecord className={classes.listIcon} />
                      </ListItemIcon>

                      <ListItemText inset style={{ padding: 0 }}>
                        <Typography className={classes.listItemTypography}>
                          {t("Hinnasto")}
                        </Typography>
                      </ListItemText>
                    </DashboardNavLink>
                  </div>
                </Badge>
              ) : null}

              {/*
              <div className={classes.navListLi}>
                <DashboardNavLink disabled className={`${classes.listItem} ${classes.nested}`} to='/dashboard/company/settings'>
                <ListItemIcon className={classes.listIconContainer}><FiberManualRecord className={classes.listIcon} /></ListItemIcon>
                <ListItemText inset style={{padding:0}}>
                  <Typography className={classes.listItemTypography}>{t('Asetukset')}</Typography>
                </ListItemText>
                </DashboardNavLink>
              </div>
              */}
            </List>
          </Collapse>

          <div className={classes.navListLi}>
            <Badge
              overlap="rectangular"
              classes={{
                anchorOriginTopRightRectangular:
                  classes.anchorOriginTopRightRectangular,
              }}
              badgeContent={
                myUser?.status == 100 &&
                loading == false &&
                myAnalytics?.drivers == 0
                  ? "!"
                  : null
              }
              color="error"
            >
              <ListItemButton onClick={() => toggleNavListOpen("drivers")}>
                <ListItemIcon>
                  <AirlineSeatReclineExtra className={classes.listMainIcon} />
                </ListItemIcon>
                <ListItemText>
                  <Typography className={classes.listItemTypography}>
                    {t("Kuljettajat")}
                  </Typography>
                </ListItemText>
              </ListItemButton>
            </Badge>
          </div>

          <Collapse in={navListOpen.drivers} timeout="auto" unmountOnExit>
            <List component="div">
              <div className={classes.navListLi}>
                <DashboardNavLink
                  className={`${classes.listItem} ${classes.nested}`}
                  to="/dashboard/drivers/list"
                >
                  <ListItemIcon className={classes.listIconContainer}>
                    <FiberManualRecord className={classes.listIcon} />
                  </ListItemIcon>
                  <ListItemText inset style={{ padding: 0 }}>
                    <Typography className={classes.listItemTypography}>
                      {t("Kaikki")}
                    </Typography>
                  </ListItemText>
                </DashboardNavLink>
              </div>

              {myUser?.status == 100 ? (
                <Badge
                  overlap="rectangular"
                  classes={{
                    anchorOriginTopRightRectangular:
                      classes.anchorOriginTopRightRectangularNested,
                  }}
                  badgeContent={
                    myUser?.status == 100 &&
                    loading == false &&
                    myAnalytics?.drivers == 0
                      ? "!"
                      : null
                  }
                  color="error"
                >
                  <div className={classes.navListLi}>
                    <DashboardNavLink
                      disabled={Boolean(myUser) && myUser.status < 100}
                      className={`${classes.listItem} ${classes.nested}`}
                      to="/dashboard/drivers/new"
                    >
                      <ListItemIcon className={classes.listIconContainer}>
                        <FiberManualRecord className={classes.listIcon} />
                      </ListItemIcon>
                      <ListItemText inset style={{ padding: 0 }}>
                        <Typography className={classes.listItemTypography}>
                          {t("Lisää uusi")}
                        </Typography>
                      </ListItemText>
                    </DashboardNavLink>
                  </div>
                </Badge>
              ) : null}
            </List>
          </Collapse>

          <div className={classes.navListLi}>
            <Badge
              overlap="rectangular"
              classes={{
                anchorOriginTopRightRectangular:
                  classes.anchorOriginTopRightRectangular,
              }}
              badgeContent={
                myUser?.status == 100 &&
                loading == false &&
                myAnalytics?.vehicles == 0
                  ? "!"
                  : null
              }
              color="error"
            >
              <ListItemButton onClick={() => toggleNavListOpen("cars")}>
                <ListItemIcon>
                  <DriveEta className={classes.listMainIcon} />
                </ListItemIcon>
                <ListItemText>
                  <Typography className={classes.listItemTypography}>
                    {t("Autot")}
                  </Typography>
                </ListItemText>
              </ListItemButton>
            </Badge>
          </div>

          <Collapse in={navListOpen.cars} timeout="auto" unmountOnExit>
            <List component="div">
              <div className={classes.navListLi}>
                <DashboardNavLink
                  className={`${classes.listItem} ${classes.nested}`}
                  to="/dashboard/vehicles/list"
                >
                  <ListItemIcon className={classes.listIconContainer}>
                    <FiberManualRecord className={classes.listIcon} />
                  </ListItemIcon>
                  <ListItemText inset style={{ padding: 0 }}>
                    <Typography className={classes.listItemTypography}>
                      {t("Kaikki")}
                    </Typography>
                  </ListItemText>
                </DashboardNavLink>
              </div>

              {myUser?.status == 100 ? (
                <Badge
                  overlap="rectangular"
                  classes={{
                    anchorOriginTopRightRectangular:
                      classes.anchorOriginTopRightRectangularNested,
                  }}
                  badgeContent={
                    loading == false && myAnalytics?.vehicles == 0 ? "!" : null
                  }
                  color="error"
                >
                  <div className={classes.navListLi}>
                    <DashboardNavLink
                      disabled={Boolean(myUser) && myUser.status < 100}
                      className={`${classes.listItem} ${classes.nested}`}
                      to="/dashboard/vehicles/new"
                    >
                      <ListItemIcon className={classes.listIconContainer}>
                        <FiberManualRecord className={classes.listIcon} />
                      </ListItemIcon>
                      <ListItemText inset style={{ padding: 0 }}>
                        <Typography className={classes.listItemTypography}>
                          {t("Lisää uusi")}
                        </Typography>
                      </ListItemText>
                    </DashboardNavLink>
                  </div>
                </Badge>
              ) : null}

              {(myUser?.status == 100 || myUser?.is_staff === true) && (
                <div className={classes.navListLi}>
                  <Link
                    to="/map"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${classes.navLinkRoot} ${classes.listItem} ${classes.nested}`}
                  >
                    <ListItemIcon className={classes.listIconContainer}>
                      <MapIcon className={classes.listMainIcon} />
                    </ListItemIcon>
                    <ListItemText>
                      <Typography className={classes.listItemTypography}>
                        {t("Kartta")}
                      </Typography>
                    </ListItemText>
                  </Link>
                </div>
              )}
            </List>
          </Collapse>

          {myUser?.status == 100 ? (
            <div className={classes.navListLi}>
              <ListItemButton onClick={() => toggleNavListOpen("orders")}>
                <ListItemIcon>
                  <Room className={classes.listMainIcon} />
                </ListItemIcon>
                <ListItemText>
                  <Typography className={classes.listItemTypography}>
                    {t("Ajetut ajot")}
                  </Typography>
                </ListItemText>
              </ListItemButton>
            </div>
          ) : null}
          <Collapse in={navListOpen.orders} timeout="auto" unmountOnExit>
            <List component="div">
              <div className={classes.navListLi}>
                <DashboardNavLink
                  className={`${classes.listItem} ${classes.nested}`}
                  to="/dashboard/orders/list"
                >
                  <ListItemIcon className={classes.listIconContainer}>
                    <FiberManualRecord className={classes.listIcon} />
                  </ListItemIcon>
                  <ListItemText inset style={{ padding: 0 }}>
                    <Typography className={classes.listItemTypography}>
                      {t("Kaikki")}
                    </Typography>
                  </ListItemText>
                </DashboardNavLink>
              </div>

              {/*
              <div className={classes.navListLi}>
                <DashboardNavLink disabled className={`${classes.listItem} ${classes.nested}`} to='/dashboard/orders/new'>
                <ListItemIcon className={classes.listIconContainer}><FiberManualRecord className={classes.listIcon} /></ListItemIcon>
                <ListItemText inset style={{padding:0}}>
                  <Typography className={classes.listItemTypography}>{t('Lisää uusi')}</Typography>
                </ListItemText>
                </DashboardNavLink>
              </div>
              */}
            </List>
          </Collapse>

          <Divider />

          <div className={classes.navListLi}>
            <DashboardNavLink to="/dashboard/settings">
              <ListItemIcon>
                <Tune className={classes.listMainIcon} />
              </ListItemIcon>
              <ListItemText>
                <Typography className={classes.listItemTypography}>
                  {t("Asetukset")}
                </Typography>
              </ListItemText>
            </DashboardNavLink>
          </div>

          <div className={classes.navListLi}>
            <DashboardNavLink to="/dashboard/support">
              <ListItemIcon>
                <ContactSupport className={classes.listMainIcon} />
              </ListItemIcon>
              <ListItemText>
                <Typography className={classes.listItemTypography}>
                  {t("Tuki")}
                </Typography>
              </ListItemText>
            </DashboardNavLink>
          </div>

          <div className={classes.navListLi}>
            <DashboardNavLink to="/dashboard/service">
              <ListItemIcon>
                <DescriptionIcon className={classes.listMainIcon} />
              </ListItemIcon>
              <ListItemText>
                <Typography className={classes.listItemTypography}>
                  {t("Palvelusopimukset ja tietosuoja")}
                </Typography>
              </ListItemText>
            </DashboardNavLink>
          </div>
        </List>

        <Divider />
      </Drawer>

      <main className={classes.content}>
        {loaded ? (
          <Routes>
            {/* Paths are relative to parent Route path="/dashboard/*" (see RR6 descendant Routes). */}
            <Route path="start" element={<Start />} />
            <Route path="company/detail" element={<CompanyDetail />} />
            <Route path="company/edit" element={<CompanyEdit />} />
            <Route path="company/payment" element={<CompanyPayment />} />
            <Route path="users" element={<UsersList />} />
            <Route path="users/invite" element={<UserInviteView />} />
            <Route path="drivers/list" element={<DriversList />} />
            <Route
              key="driver_new"
              path="drivers/new"
              element={<DriverEdit />}
            />
            <Route
              key="driver_edit"
              path="drivers/:driverId/edit"
              element={<DriverEdit />}
            />
            <Route path="vehicles/list" element={<VehiclesList />} />
            <Route
              key="vehicle_new"
              path="vehicles/new"
              element={<VehicleEdit />}
            />
            <Route
              key="vehicle_edit"
              path="vehicles/:vehicleId/edit"
              element={<VehicleEdit />}
            />
            <Route
              key="vehicle_view"
              path="vehicles/:vehicleId/view"
              element={<VehicleView />}
            />
            <Route
              key="prices_list"
              path="prices/list"
              element={<PricesList />}
            />
            <Route path="orders/list" element={<OrdersList />} />
            <Route
              path="orders/:orderId/detail"
              element={<OrderDetailView />}
            />
            <Route
              path="preorders/:preorderId/detail"
              element={<PreorderDetailView />}
            />
            <Route path="service" element={<Service />} />
            <Route path="service/prices" element={<Prices />} />
            <Route path="settings" element={<Settings />} />
            <Route path="support" element={<Support />} />
            <Route
              path="drivers"
              element={<Navigate to="/dashboard/drivers/list" replace />}
            />
            <Route
              path="vehicles"
              element={<Navigate to="/dashboard/vehicles/list" replace />}
            />
            <Route
              path="*"
              element={<Navigate to="/dashboard/start" replace />}
            />
          </Routes>
        ) : null}
      </main>
    </div>
  );
};

Dashboard.propTypes = {};

export default Dashboard;
